import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { REVEAL } from "@/lib/motion";
import { INDUSTRIES } from "@/config/industries";
import { SaasIcon, ClinicIcon, EducationIcon, FitnessIcon, MapIcon } from "@/components/illustrations/LineIcons";

const META = {
  "b2b-saas-and-tech": { segment: "B2B", icon: SaasIcon },
  "clinics-and-aesthetic-practices": { segment: "Local", icon: ClinicIcon },
  "education-and-tuition-centres": { segment: "Local", icon: EducationIcon },
  "fitness-studios": { segment: "Local", icon: FitnessIcon },
};

const FILTERS = [
  { label: "All", value: "All" },
  { label: "B2B", value: "B2B" },
  { label: "Local businesses", value: "Local" },
];

export const HomeIndustries = () => {
  const [filter, setFilter] = useState("All");
  const shown = INDUSTRIES.filter((i) => filter === "All" || META[i.slug].segment === filter);

  return (
    <section className="px-6 sm:px-8 pb-20 sm:pb-28" data-testid="home-industries">
      <div className="max-w-5xl mx-auto">
        <motion.h2 {...REVEAL} className="font-fraunces font-extrabold text-3xl sm:text-4xl tracking-tight text-[#1F1F1C]">
          Built for how your business sells.
        </motion.h2>

        <div className="mt-8 flex gap-2" role="tablist" data-testid="industry-filters">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setFilter(f.value)}
              role="tab"
              aria-selected={filter === f.value}
              className={`btn-press rounded-2xl px-5 py-2.5 text-sm font-semibold border ${
                filter === f.value
                  ? "bg-[#3D472D] text-[#F4E9D8] border-[#3D472D]"
                  : "border-[#1F1F1C]/20 text-[#4A463F] hover:border-[#C8703A] hover:text-[#C8703A]"
              }`}
              data-testid={`industry-filter-${f.value.toLowerCase()}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {shown.map((ind) => {
            const Icon = META[ind.slug].icon;
            return (
              <motion.div key={ind.slug} layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
                <Link
                  to={`/industries/${ind.slug}`}
                  className="card-lift h-full flex flex-col bg-[#EADCC6] rounded-2xl border border-[#1F1F1C]/10 p-7"
                  data-testid={`home-industry-${ind.slug}`}
                >
                  <Icon className="w-10 h-10 text-[#C8703A]" />
                  <h3 className="mt-6 font-fraunces font-extrabold text-base md:text-lg text-[#1F1F1C]">{ind.name}</h3>
                  <p className="mt-3 text-sm text-[#4A463F] leading-relaxed flex-1">{ind.headline}</p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#C8703A]">
                    See the workflows <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
                  </span>
                </Link>
              </motion.div>
            );
          })}

          <motion.div layout initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
            <Link
              to="/audit"
              className="card-lift h-full flex flex-col bg-[#F4E9D8] rounded-2xl border border-dashed border-[#1F1F1C]/25 p-7"
              data-testid="home-industry-not-listed"
            >
              <MapIcon className="w-10 h-10 text-[#4A463F]" />
              <h3 className="mt-6 font-fraunces font-extrabold text-base md:text-lg text-[#1F1F1C]">
                Not listed? Book an audit and we'll map it.
              </h3>
              <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[#C8703A]">
                Book a free sales audit <ArrowRight className="w-3.5 h-3.5" strokeWidth={2.5} />
              </span>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HomeIndustries;
